import {
  Center,
  Image,
  Input,
  Paper,
  SegmentedControl,
  Stack,
  Text
} from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { FormattedMessage } from 'react-intl'
import VpLogo from '@/assets/vp-stripes.png'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { MilestonePipelineIcon } from '@/components/vlm/MilestonePipelineIcon'
import { MilestoneCamera, PatrolVideoSource } from '@/types/vlm'
import { MilestoneServerPicker } from '../MilestoneServerPicker'
import { PipelineFormValues } from '../VlmPipelineForm'
import { MilestoneCameraSelection } from '../cameras/MilestoneCameraSelection'
import { RegisteredCameraSelection } from '../cameras/RegisteredCameraSelection'

type PatrolSourceSettingsProps = {
  form: UseFormReturnType<PipelineFormValues>
}

export const PatrolSourceSettings = ({ form }: PatrolSourceSettingsProps) => {
  const { deviceId, actions } = form.values
  const { patrolServerId, patrolVideoSource, patrolCameras } =
    form.values.source

  const handleVideoSourceChange = (value: string) => {
    const videoSource = value as PatrolVideoSource
    if (videoSource === patrolVideoSource) return

    form.setFieldValue('source.patrolVideoSource', videoSource)
    form.setFieldValue('source.patrolCameras', [])

    if (videoSource === 'rtsp') {
      form.setFieldValue('source.patrolServerId', '')

      const emailActions = actions.filter(
        (action) => action.type !== 'milestone'
      )
      form.setFieldValue('actions', emailActions)
      form.setFieldValue(
        'source.patrolMilestoneActionsRemoved',
        emailActions.length !== actions.length
      )
    } else {
      form.setFieldValue('source.patrolMilestoneActionsRemoved', false)
    }
  }

  const handleServerChange = (serverId: string) => {
    form.setFieldValue('source.patrolServerId', serverId)
    // Cameras belong to a server; a different server needs a new selection.
    if (serverId !== patrolServerId)
      form.setFieldValue('source.patrolCameras', [])
  }

  const handleCamerasChange = (cameras: MilestoneCamera[]) =>
    form.setFieldValue('source.patrolCameras', cameras)

  return (
    <Stack gap="xl">
      <Input.Wrapper
        label={
          <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.label" />
        }
        description={
          <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.description" />
        }
      >
        <Paper my={4} p="sm" withBorder>
          <SegmentedControl
            size="md"
            radius="md"
            color="brand-primary"
            value={patrolVideoSource}
            data={[
              {
                value: 'milestone',
                label: (
                  <Center style={{ gap: 10 }}>
                    <MilestonePipelineIcon size={20} />
                    <span>
                      <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.milestone" />
                    </span>
                  </Center>
                )
              },
              {
                value: 'rtsp',
                label: (
                  <Center style={{ gap: 10 }}>
                    <Image src={VpLogo} w={20} h={20} fit="contain" />
                    <span>
                      <FormattedMessage id="vlm.pipelines.create.patrol.videoSource.rtsp" />
                    </span>
                  </Center>
                )
              }
            ]}
            fullWidth
            onChange={handleVideoSourceChange}
          />
        </Paper>
      </Input.Wrapper>

      {patrolVideoSource === 'rtsp' ? (
        deviceId && (
          <RegisteredCameraSelection
            deviceId={deviceId}
            value={patrolCameras}
            error={form.errors['source.patrolCameras']}
            description={
              <FormattedMessage id="vlm.pipelines.create.patrol.cameras.registeredIntro" />
            }
            onChange={handleCamerasChange}
          />
        )
      ) : (
        <>
          <MilestoneServerPicker
            value={patrolServerId}
            label={
              <FormattedMessage id="vlm.pipelines.create.milestone.label" />
            }
            description={
              <FormattedMessage id="vlm.pipelines.create.patrol.server.description" />
            }
            error={form.errors['source.patrolServerId']}
            deviceId={deviceId}
            skeletonCount={6}
            onChange={handleServerChange}
          />

          {/* Cameras are listed through the device, from the chosen server. */}
          {deviceId && patrolServerId && (
            <MilestoneCameraSelection
              serverId={patrolServerId}
              deviceId={deviceId}
              value={patrolCameras}
              error={form.errors['source.patrolCameras']}
              description={
                <>
                  <Text size="sm" c="dimmed">
                    <FormattedMessage id="vlm.pipelines.create.patrol.cameras.intro" />
                  </Text>
                  <PlatformAlert variant="info" compact>
                    <FormattedMessage id="vlm.pipelines.create.patrol.cameras.recordingHint" />
                  </PlatformAlert>
                </>
              }
              onChange={handleCamerasChange}
            />
          )}
        </>
      )}
    </Stack>
  )
}
